import { useEffect, useMemo } from 'react';

import { useGvCompetitors } from './hooks.persistence';
import { getScopedRivalOptions, keepOffered, NO_RIVALS_ID } from './gvScope';
import type { FilterOption } from './components/posFilterTypes';

interface RivalFilterOptions {
  rival_refs?: { id: number; name: string }[];
  rival_custom_refs?: { id: number; name: string }[];
  client_rival_refs?: Record<string, number[]>;
  client_custom_rival_refs?: Record<string, number[]>;
}

interface Params {
  isBranded: boolean;
  apiFilters: RivalFilterOptions;
  clientRefs: FilterOption[];
  clientCustomRefs: FilterOption[];
  // Options loaded: safe to drop selections that are no longer offered.
  ready: boolean;
}

// Competitor picker state. The offered competitors follow the selected
// products/brands (union of their rivals; custom-brand rivals for brand
// accounts) and persisted picks that are no longer offered are dropped.
export const useGvCompetitorScope = ({
  isBranded,
  apiFilters,
  clientRefs,
  clientCustomRefs,
  ready
}: Params) => {
  const [competitors, setCompetitors] = useGvCompetitors();

  const customRivalOptions = useMemo(
    () =>
      getScopedRivalOptions(
        clientCustomRefs,
        apiFilters.client_custom_rival_refs,
        apiFilters.rival_custom_refs || []
      ),
    [
      clientCustomRefs,
      apiFilters.client_custom_rival_refs,
      apiFilters.rival_custom_refs
    ]
  );
  const competitorOptions = useMemo(
    () =>
      isBranded
        ? customRivalOptions
        : getScopedRivalOptions(
            clientRefs,
            apiFilters.client_rival_refs,
            apiFilters.rival_refs || []
          ),
    [
      isBranded,
      customRivalOptions,
      clientRefs,
      apiFilters.client_rival_refs,
      apiFilters.rival_refs
    ]
  );

  useEffect(() => {
    if (!ready) return;
    const offered = new Set(competitorOptions.map((option) => option.value));
    setCompetitors((previous) => keepOffered(previous, offered, (id) => id));
  }, [setCompetitors, ready, competitorOptions]);

  // "None" is sent as the NO_RIVALS_ID sentinel (the client's own refs only).
  const selectedRivalFilter = useMemo(() => {
    const picked = competitors.map(Number).filter(Number.isFinite);
    const ids = picked.length > 0 ? picked : [NO_RIVALS_ID];
    return {
      selected_rival_ref_ids: isBranded ? null : ids,
      selected_rival_custom_ids: isBranded ? ids : null
    };
  }, [isBranded, competitors]);

  return {
    competitors,
    setCompetitors,
    competitorOptions,
    customRivalOptions,
    selectedRivalFilter
  };
};
